import { Component, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DocumentService } from '../../services/document.service';
import { UserSessionService } from '../../services/user-session.service';
import { API_BASE_URL } from '../../services/api-base';
import { UserService } from '../../services/user.service';

type RecipientStatus = 'PENDING' | 'DONE';

interface DispatchOverview {
  id: number;
  document_id: number;
  sent_at: string;
  sent_type: string;
  sent_by?: number;
  sent_by_name?: string;
  total_recipients: number;
  pending_recipients: number;
  done_recipients: number;
}

interface DocumentOverview {
  id: number;
  title: string;
  created_at: string;
  created_by?: number;
  author_name?: string;
  dispatches_count: number;
  total_recipients: number;
  pending_recipients: number;
  done_recipients: number;
  actions_count: number;
}

interface RecipientRow {
  recipient_id: number;
  dispatch_id?: number;
  document_id: number;
  title: string;
  user_id: number;
  user_name: string;
  email: string;
  sent_type?: string;
  can_resend_email?: number;
  status: RecipientStatus;
  sent_at: string;
  last_action_at?: string;
  file_path?: string;
}

@Component({
  selector: 'app-controlsign',
  templateUrl: './controlsign.component.html',
  styleUrls: ['./controlsign.component.scss']
})
export class ControlsignComponent implements OnInit {
  activeMainTab: 'dashboard' | 'documents' = 'dashboard';
  currentUser: any = null;
  documents: DocumentOverview[] = [];
  filteredDocuments: DocumentOverview[] = [];
  pagedDocuments: DocumentOverview[] = [];
  loadingDocuments = false;
  documentsError = '';
  searchTerm = '';
  pageSize = 6;
  currentPage = 1;

  registerModalOpen = false;
  registerPageOpen = false;
  registerDetailsOpen = false;
  registerForm = {
    title: '',
    document_type: 'POLICY',
    send_type: 'EMAIL',
    description: '',
    require_read: true,
    require_accept: false,
    require_agreement: false,
    require_signature: false,
    require_selfie: false,
    require_geolocation: false
  };
  registerFile: File | null = null;
  registerPreviewUrl: string | null = null;
  registerPreviewSafeUrl: SafeResourceUrl | null = null;
  isDraggingFile = false;
  signerEmailInput = '';
  signerEmails: string[] = [];
  registerFeedback = '';
  registerLoading = false;
  documentTypeOptions = [
    { labelKey: 'INTRANET_DASH.DOC_TYPE.POLICY', value: 'POLICY' },
    { labelKey: 'INTRANET_DASH.DOC_TYPE.CONTRACT', value: 'CONTRACT' },
    { labelKey: 'INTRANET_DASH.DOC_TYPE.TERM', value: 'TERM' },
    { labelKey: 'INTRANET_DASH.DOC_TYPE.NOTICE', value: 'NOTICE' },
    { labelKey: 'INTRANET_DASH.DOC_TYPE.OTHER', value: 'OTHER' }
  ];

  activeDocument: DocumentOverview | null = null;
  recipientsLoading: { pending: boolean; done: boolean } = { pending: false, done: false };
  resendingRecipientIds = new Set<number>();
  pendingRecipients: RecipientRow[] = [];
  completedRecipients: RecipientRow[] = [];
  pendingModalOpen = false;
  completedModalOpen = false;
  pendingSearch = '';
  completedSearch = '';
  pendingPage = 1;
  completedPage = 1;
  recipientsPageSize = 6;
  deletingDocumentId: number | null = null;
  deleteFeedback = '';
  expandedDocumentId: number | null = null;
  expandedDocumentTitle = '';
  dispatches: DispatchOverview[] = [];
  dispatchesLoading = false;
  dispatchesError = '';
  dispatchesPage = 1;
  dispatchesPageSize = 5;
  dispatchesTotal = 0;
  dispatchFeedback = '';
  selectedDocumentForActions: DocumentOverview | null = null;
  selectedDispatchForActions: DispatchOverview | null = null;
  dispatchDeleteId: number | null = null;

  private readonly apiBase = `${API_BASE_URL}`;
  private readonly productionUploadsBaseUrl = 'https://controlps.app';

  constructor(
    private documentService: DocumentService,
    private userSession: UserSessionService,
    private userService: UserService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.currentUser = this.userSession.getUser();
    this.loadDocuments();
  }

  loadDocuments() {
    if (!this.currentUser?.tenant_id) {
      return;
    }
    this.loadingDocuments = true;
    this.documentsError = '';
    this.documentService.getOverview(this.currentUser.tenant_id).subscribe({
      next: (docs) => {
        this.documents = docs;
        this.applyDocumentFilter();
        this.loadingDocuments = false;
      },
      error: (err) => {
        this.documentsError = err?.error?.error || 'Não foi possível carregar os documentos.';
        this.loadingDocuments = false;
      }
    });
  }

  toggleDocumentDispatches(doc: DocumentOverview) {
    if (this.expandedDocumentId === doc.id) {
      this.expandedDocumentId = null;
      this.dispatchFeedback = '';
      return;
    }
    this.expandedDocumentId = doc.id;
    this.expandedDocumentTitle = doc.title;
    this.dispatchesPage = 1;
    this.dispatchFeedback = '';
    this.loadDocumentDispatches(doc.id, 1);
  }

  private loadDocumentDispatches(documentId: number, page: number) {
    if (!this.currentUser?.tenant_id) {
      return;
    }
    this.dispatchesLoading = true;
    this.dispatchesError = '';
    this.documentService.getDispatches(documentId, this.currentUser.tenant_id, page, this.dispatchesPageSize).subscribe({
      next: (response) => {
        this.dispatches = response.items;
        this.dispatchesTotal = response.total;
        this.dispatchesLoading = false;
      },
      error: (err) => {
        this.dispatchesLoading = false;
        this.dispatchesError = err?.error?.error || 'Não foi possível carregar os disparos.';
      }
    });
  }

  changeDispatchPage(direction: number) {
    if (!this.expandedDocumentId) {
      return;
    }
    const totalPages = this.dispatchesTotalPages;
    this.dispatchesPage = Math.min(Math.max(1, this.dispatchesPage + direction), totalPages);
    this.loadDocumentDispatches(this.expandedDocumentId, this.dispatchesPage);
  }

  get dispatchesTotalPages() {
    return Math.max(1, Math.ceil(this.dispatchesTotal / this.dispatchesPageSize));
  }

  getDocumentRows() {
    if (this.expandedDocumentId) {
      return this.filteredDocuments.filter(doc => doc.id === this.expandedDocumentId);
    }
    return this.pagedDocuments;
  }

  get isMasterUser() {
    const roleName = String(this.currentUser?.role_name ?? this.currentUser?.role?.role_name ?? this.currentUser?.role?.name ?? '').toLowerCase();
    const roleId = Number(this.currentUser?.role_id || 0);
    const hasMasterFlag = Boolean(this.currentUser?.['is_master'] ?? this.currentUser?.['isMaster'] ?? this.currentUser?.['master']);
    return hasMasterFlag || roleId === 1 || roleName.includes('master') || roleName.includes('admin');
  }

  canDeleteDocument(doc: DocumentOverview) {
    if (this.isMasterUser) {
      return true;
    }
    return doc.actions_count === 0;
  }

  deleteDocument(doc: DocumentOverview) {
    if (!this.currentUser?.tenant_id) {
      return;
    }
    this.deleteFeedback = '';
    if (!this.canDeleteDocument(doc)) {
      this.deleteFeedback = 'Não é possível excluir este documento porque já houve interações.';
      return;
    }
    if (!confirm('Deseja realmente excluir este documento?')) {
      return;
    }
    this.deleteFeedback = '';
    this.deletingDocumentId = doc.id;
    this.documentService.deleteDocument(doc.id, this.currentUser.tenant_id, this.currentUser?.id).subscribe({
      next: () => {
        this.deletingDocumentId = null;
        this.loadDocuments();
      },
      error: (err) => {
        this.deletingDocumentId = null;
        this.deleteFeedback = err?.error?.error || 'Não foi possível excluir o documento.';
      }
    });
  }

  applyDocumentFilter() {
    if (!this.searchTerm) {
      this.filteredDocuments = [...this.documents];
    } else {
      const term = this.searchTerm.toLowerCase();
      this.filteredDocuments = this.documents.filter(doc =>
        doc.title.toLowerCase().includes(term) ||
        (doc.author_name || '').toLowerCase().includes(term)
      );
    }
    this.currentPage = 1;
    this.updatePagedDocuments();
  }

  updatePagedDocuments() {
    const start = (this.currentPage - 1) * this.pageSize;
    this.pagedDocuments = this.filteredDocuments.slice(start, start + this.pageSize);
  }

  changeDocumentPage(direction: number) {
    const totalPages = this.totalDocumentPages;
    this.currentPage = Math.min(Math.max(1, this.currentPage + direction), totalPages);
    this.updatePagedDocuments();
  }

  openRegisterModal() {
    this.activeMainTab = 'documents';
    this.registerModalOpen = true;
    this.registerPageOpen = true;
    this.registerFeedback = '';
    if (!this.registerForm.send_type) {
      this.registerForm.send_type = 'EMAIL';
    }
  }

  closeRegisterModal() {
    this.registerModalOpen = false;
    this.registerPageOpen = false;
    this.registerDetailsOpen = false;
    this.registerFile = null;
    if (this.registerPreviewUrl) {
      URL.revokeObjectURL(this.registerPreviewUrl);
    }
    this.registerPreviewUrl = null;
    this.registerPreviewSafeUrl = null;
    this.signerEmailInput = '';
    this.signerEmails = [];
    this.registerFeedback = '';
    this.registerForm = {
      title: '',
      document_type: 'POLICY',
      send_type: 'EMAIL',
      description: '',
      require_read: true,
      require_accept: false,
      require_agreement: false,
      require_signature: false,
      require_selfie: false,
      require_geolocation: false
    };
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    if (file) {
      this.setSelectedFile(file);
    }
  }

  private setSelectedFile(file: File | null) {
    this.registerFile = file;
    if (this.registerPreviewUrl) {
      URL.revokeObjectURL(this.registerPreviewUrl);
      this.registerPreviewUrl = null;
    }
    this.registerPreviewSafeUrl = null;
    if (file) {
      this.registerPreviewUrl = URL.createObjectURL(file);
      const previewUrl = `${this.registerPreviewUrl}#toolbar=0&navpanes=0&scrollbar=1&zoom=page-width`;
      this.registerPreviewSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(previewUrl);
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDraggingFile = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDraggingFile = false;
  }

  onFileDrop(event: DragEvent) {
    event.preventDefault();
    this.isDraggingFile = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.setSelectedFile(file);
    }
  }

  submitDocument() {
    if (!this.currentUser?.tenant_id || !this.currentUser?.id) {
      this.registerFeedback = 'Usuário inválido.';
      return;
    }
    if (!this.registerForm.send_type) {
      this.registerFeedback = 'Selecione o tipo de envio.';
      return;
    }
    if (!this.registerFile) {
      this.registerFeedback = 'Escolha um arquivo PDF.';
      return;
    }
    const normalizedEmailsInput = Array.from(new Set(this.signerEmails.map((email) => this.normalizeEmail(email)).filter(Boolean)));
    if (this.registerForm.send_type === 'EMAIL' && !normalizedEmailsInput.length) {
      this.registerFeedback = 'Informe ao menos um e-mail para envio externo.';
      return;
    }
    const formData = new FormData();
    const isEmailDispatch = this.registerForm.send_type === 'EMAIL';
    formData.append('tenant_id', this.currentUser.tenant_id);
    formData.append('title', this.registerForm.title);
    formData.append('description', this.registerForm.description);
    formData.append('document_type', this.registerForm.document_type);
    formData.append('send_type', this.registerForm.send_type);
    formData.append('require_read', String(!isEmailDispatch && this.registerForm.require_read ? 1 : 0));
    formData.append('require_accept', String(!isEmailDispatch && this.registerForm.require_accept ? 1 : 0));
    formData.append('require_agreement', String(!isEmailDispatch && this.registerForm.require_agreement ? 1 : 0));
    formData.append('require_signature', String(!isEmailDispatch && this.registerForm.require_signature ? 1 : 0));
    formData.append('require_selfie', String(!isEmailDispatch && this.registerForm.require_selfie ? 1 : 0));
    formData.append('require_geolocation', String(!isEmailDispatch && this.registerForm.require_geolocation ? 1 : 0));
    formData.append('created_by', this.currentUser.id);
    formData.append('file', this.registerFile);

    this.registerLoading = true;
    this.documentService.createDocument(formData).subscribe({
      next: (created) => {
        const finalize = (message?: string) => {
          this.registerLoading = false;
          this.closeRegisterModal();
          this.loadDocuments();
          if (message) {
            this.dispatchFeedback = message;
          }
        };
        const normalizedEmails = normalizedEmailsInput;
        if (!normalizedEmails.length) {
          finalize('Documento criado com sucesso.');
          return;
        }
        const documentId = Number(created?.id || 0);
        if (!documentId) {
          finalize('Documento criado, mas não foi possível preparar o envio para signatários.');
          return;
        }
        if (this.registerForm.send_type === 'EMAIL') {
          this.documentService.dispatchDocument({
            tenant_id: Number(this.currentUser.tenant_id),
            document_id: documentId,
            sent_type: 'EMAIL',
            sent_by: Number(this.currentUser.id),
            recipient_emails: normalizedEmails
          }).subscribe({
            next: (response) => {
              const sentCount = Number(response?.recipients || normalizedEmails.length);
              const failedCount = Array.isArray(response?.email_failures) ? response.email_failures.length : 0;
              if (failedCount > 0) {
                finalize(`Documento criado. ${sentCount - failedCount}/${sentCount} e-mail(s) enviado(s).`);
                return;
              }
              finalize(`Documento criado e enviado para ${sentCount} signatário(s).`);
            },
            error: (err) => {
              finalize(err?.error?.error || 'Documento criado, mas não foi possível enviar os e-mails.');
            }
          });
          return;
        }
        this.userService.getUsersByTenant(Number(this.currentUser.tenant_id)).subscribe({
          next: (users) => {
            const emailToUserId = new Map<string, number>();
            (Array.isArray(users) ? users : []).forEach((user: any) => {
              const candidates = [
                user?.email,
                user?.personal_email,
                user?.corporate_email
              ].map((value) => this.normalizeEmail(value));
              candidates.forEach((email) => {
                if (email && !emailToUserId.has(email)) {
                  emailToUserId.set(email, Number(user.id));
                }
              });
            });

            const matchedIds = Array.from(
              new Set(
                normalizedEmails
                  .map((email) => emailToUserId.get(email))
                  .filter((id) => Number(id) > 0) as number[]
              )
            );
            const notFound = normalizedEmails.filter((email) => !emailToUserId.has(email));

            if (!matchedIds.length) {
              finalize(`Documento criado. Nenhum e-mail informado pertence a usuários deste ambiente. (${notFound.join(', ')})`);
              return;
            }

            this.documentService.dispatchDocument({
              tenant_id: Number(this.currentUser.tenant_id),
              document_id: documentId,
              sent_type: 'SPECIFIC_USERS',
              sent_by: Number(this.currentUser.id),
              user_ids: matchedIds
            }).subscribe({
              next: (response) => {
                const sentCount = Number(response?.recipients || matchedIds.length);
                const suffix = notFound.length ? ` E-mails não encontrados: ${notFound.join(', ')}` : '';
                finalize(`Documento criado e enviado para ${sentCount} signatário(s).${suffix}`);
              },
              error: (err) => {
                finalize(err?.error?.error || 'Documento criado, mas não foi possível enviar para os signatários.');
              }
            });
          },
          error: () => {
            finalize('Documento criado, mas não foi possível validar os e-mails dos signatários.');
          }
        });
      },
      error: (err) => {
        this.registerLoading = false;
        this.registerFeedback = err?.error?.error || 'Não foi possível cadastrar o documento.';
      }
    });
  }

  openRecipientsModal(doc: DocumentOverview, status: RecipientStatus) {
    this.activeDocument = doc;
    if (status === 'PENDING') {
      this.pendingModalOpen = true;
      this.pendingSearch = doc.title;
      this.pendingPage = 1;
    } else {
      this.completedModalOpen = true;
      this.completedSearch = '';
      this.completedPage = 1;
    }
    this.loadRecipients(status, doc.id);
  }

  openDispatchRecipients(dispatch: DispatchOverview, status: RecipientStatus) {
    const doc = this.documents.find(d => d.id === dispatch.document_id) || null;
    this.activeDocument = doc;
    if (status === 'PENDING') {
      this.pendingModalOpen = true;
      this.pendingSearch = doc?.title || '';
      this.pendingPage = 1;
    } else {
      this.completedModalOpen = true;
      this.completedSearch = '';
      this.completedPage = 1;
    }
    this.loadRecipients(status, dispatch.document_id, { dispatchId: dispatch.id });
  }

  deleteDispatch(dispatch: DispatchOverview) {
    if (!this.currentUser?.tenant_id) {
      return;
    }
    if (!confirm('Deseja realmente excluir este disparo e todos os dados associados?')) {
      return;
    }
    this.dispatchFeedback = '';
    this.dispatchDeleteId = dispatch.id;
    this.documentService.deleteDispatch(dispatch.id, this.currentUser.tenant_id).subscribe({
      next: () => {
        this.dispatchDeleteId = null;
        this.dispatchFeedback = 'Disparo excluído com sucesso.';
        if (this.expandedDocumentId) {
          this.loadDocumentDispatches(this.expandedDocumentId, this.dispatchesPage);
        }
      },
      error: (err) => {
        this.dispatchDeleteId = null;
        this.dispatchFeedback = err?.error?.error || 'Não foi possível excluir o disparo.';
      }
    });
  }

  openSelectedDocumentRecipients(status: RecipientStatus) {
    if (!this.selectedDocumentForActions) {
      return;
    }
    this.openRecipientsModal(this.selectedDocumentForActions, status);
  }

  deleteSelectedDocument() {
    if (!this.selectedDocumentForActions) {
      return;
    }
    this.deleteDocument(this.selectedDocumentForActions);
  }

  canDeleteSelectedDocument() {
    if (!this.selectedDocumentForActions) {
      return false;
    }
    return this.canDeleteDocument(this.selectedDocumentForActions);
  }

  openSelectedDispatchRecipients(status: RecipientStatus) {
    if (!this.selectedDispatchForActions) {
      return;
    }
    this.openDispatchRecipients(this.selectedDispatchForActions, status);
  }

  deleteSelectedDispatch() {
    if (!this.selectedDispatchForActions) {
      return;
    }
    this.deleteDispatch(this.selectedDispatchForActions);
  }

  private loadRecipients(status: RecipientStatus, documentId: number, options?: { dispatchId?: number }) {
    if (!this.currentUser?.tenant_id) {
      return;
    }
    this.recipientsLoading[status === 'PENDING' ? 'pending' : 'done'] = true;
    this.documentService.getRecipientsList({
      tenant_id: this.currentUser.tenant_id,
      status,
      document_id: documentId,
      dispatch_id: options?.dispatchId
    }).subscribe({
      next: (rows) => {
        if (status === 'PENDING') {
          this.pendingRecipients = rows;
        } else {
          this.completedRecipients = rows;
        }
        this.recipientsLoading[status === 'PENDING' ? 'pending' : 'done'] = false;
      },
      error: () => {
        if (status === 'PENDING') {
          this.pendingRecipients = [];
        } else {
          this.completedRecipients = [];
        }
        this.recipientsLoading[status === 'PENDING' ? 'pending' : 'done'] = false;
      }
    });
  }

  filteredPending() {
    if (!this.pendingSearch) {
      return this.pendingRecipients;
    }
    const term = this.pendingSearch.toLowerCase();
    return this.pendingRecipients.filter(row => row.title.toLowerCase().includes(term));
  }

  filteredCompleted() {
    if (!this.completedSearch) {
      return this.completedRecipients;
    }
    const term = this.completedSearch.toLowerCase();
    return this.completedRecipients.filter(row => (row.user_name || '').toLowerCase().includes(term));
  }

  paginateRecipients(list: RecipientRow[], page: number) {
    const start = (page - 1) * this.recipientsPageSize;
    return list.slice(start, start + this.recipientsPageSize);
  }

  closePendingModal() {
    this.pendingModalOpen = false;
    this.activeDocument = null;
  }

  closeCompletedModal() {
    this.completedModalOpen = false;
    this.activeDocument = null;
  }

  canResendEmail(item: RecipientRow) {
    return Number(item?.can_resend_email || 0) === 1;
  }

  resendRecipientEmail(item: RecipientRow) {
    const recipientId = Number(item?.recipient_id || 0);
    if (!recipientId || !this.currentUser?.id) {
      return;
    }
    if (!this.canResendEmail(item)) {
      return;
    }
    this.resendingRecipientIds.add(recipientId);
    this.documentService.resendRecipientEmail(recipientId, { sent_by: Number(this.currentUser.id) }).subscribe({
      next: (response) => {
        const sentAt = response?.sent_at || new Date().toISOString();
        this.pendingRecipients = this.pendingRecipients.map((row) =>
          row.recipient_id === recipientId ? { ...row, sent_at: sentAt } : row
        );
        if (this.expandedDocumentId) {
          this.loadDocumentDispatches(this.expandedDocumentId, this.dispatchesPage);
        }
        this.dispatchFeedback = 'E-mail reenviado com sucesso.';
        this.resendingRecipientIds.delete(recipientId);
      },
      error: (err) => {
        this.dispatchFeedback = err?.error?.error || 'Não foi possível reenviar o e-mail.';
        this.resendingRecipientIds.delete(recipientId);
      }
    });
  }

  downloadProof(recipient: RecipientRow) {
    const url = this.documentService.getProofPdfUrl(recipient.recipient_id);
    if (typeof window !== 'undefined') {
      window.open(url, '_blank');
    }
  }

  documentLink(path?: string | null) {
    if (!path) {
      return null;
    }
    if (path.startsWith('http')) {
      return path;
    }
    if (typeof window !== 'undefined' && window.location.hostname === 'localhost' && path.startsWith('/uploads/')) {
      return `${this.productionUploadsBaseUrl}${path}`;
    }
    return `${this.apiBase}${path}`;
  }

  get selectedFileName() {
    return this.registerFile?.name || '';
  }

  get canSubmitDocument() {
    if (!this.registerFile) {
      return false;
    }
    if (this.registerForm.send_type === 'EMAIL' && !this.signerEmails.length) {
      return false;
    }
    return true;
  }

  toggleRegisterDetails() {
    this.registerDetailsOpen = !this.registerDetailsOpen;
  }

  get dashboardTotalRecipients() {
    return this.documents.reduce((sum, doc) => sum + Number(doc.total_recipients || 0), 0);
  }

  get dashboardTotalPending() {
    return this.documents.reduce((sum, doc) => sum + Number(doc.pending_recipients || 0), 0);
  }

  get dashboardTotalDone() {
    return this.documents.reduce((sum, doc) => sum + Number(doc.done_recipients || 0), 0);
  }

  get recentDocuments() {
    return this.documents.slice(0, 6);
  }

  setMainTab(tab: 'dashboard' | 'documents') {
    this.activeMainTab = tab;
    if (tab === 'dashboard') {
      this.registerPageOpen = false;
      this.registerModalOpen = false;
    }
  }

  addSignerEmail() {
    const normalized = this.normalizeEmail(this.signerEmailInput);
    if (!normalized) {
      return;
    }
    if (!this.signerEmails.includes(normalized)) {
      this.signerEmails = [...this.signerEmails, normalized];
    }
    this.signerEmailInput = '';
  }

  removeSignerEmail(email: string) {
    this.signerEmails = this.signerEmails.filter((item) => item !== email);
  }

  private normalizeEmail(value: any) {
    return String(value || '').trim().toLowerCase();
  }

  get totalDocumentPages() {
    return Math.max(1, Math.ceil((this.filteredDocuments.length || 1) / this.pageSize));
  }

  movePendingPage(direction: number) {
    const total = this.pendingTotalPages;
    this.pendingPage = Math.min(Math.max(1, this.pendingPage + direction), total);
  }

  moveCompletedPage(direction: number) {
    const total = this.completedTotalPages;
    this.completedPage = Math.min(Math.max(1, this.completedPage + direction), total);
  }

  get pendingTotalPages() {
    const total = this.filteredPending().length || 1;
    return Math.max(1, Math.ceil(total / this.recipientsPageSize));
  }

  get completedTotalPages() {
    const total = this.filteredCompleted().length || 1;
    return Math.max(1, Math.ceil(total / this.recipientsPageSize));
  }
}
